import React from "react";

function formatClock(mins) {
  const m = ((Math.round(mins) % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const ap = h < 12 ? "AM" : "PM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(mm).padStart(2, "0")} ${ap}`;
}

function formatDur(mins) {
  const m = Math.max(0, Math.round(Number(mins) || 0));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}:${String(mm).padStart(2, "0")}`;
}

function parseClock(text) {
  const s = String(text || "").trim();
  if (!s) return null;
  const m = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2]);
  if (min > 59) return null;
  const ap = m[3] && m[3].toLowerCase();
  if (ap) {
    if (h < 1 || h > 12) return null;
    if (ap === "pm" && h !== 12) h += 12;
    if (ap === "am" && h === 12) h = 0;
  } else if (h > 23) {
    return null;
  }
  return h * 60 + min;
}

function parseDuration(text) {
  const s = String(text || "").trim();
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (min > 59) return null;
  const total = h * 60 + min;
  if (total <= 0 || total > 1440) return null;
  return total;
}

function rowsFromValve(valve) {
  const n = valve && valve.windowed ? 1 : 6;
  const events = (valve && valve.events) || [];
  const rows = [];
  for (let i = 0; i < n; i++) {
    const ev = events[i];
    rows.push({
      on: !!ev,
      start: ev ? formatClock(ev.dayMins) : "",
      duration: ev ? formatDur(ev.durMins) : "",
      recipe: ev && ev.recipe ? ev.recipe : "",
    });
  }
  return rows;
}

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(20, 28, 38, 0.45)",
  zIndex: 80,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "48px 16px",
  overflowY: "auto",
};

const card = {
  width: "min(760px, 100%)",
  background: "#fff",
  borderRadius: 10,
  boxShadow: "0 16px 48px rgba(0,0,0,0.28)",
  padding: "18px 20px 16px",
  fontFamily: "system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  color: "#26303c",
};

const inputStyle = {
  width: "100%",
  minWidth: 0,
  fontSize: 14,
  padding: "6px 8px",
  border: "1px solid #cfd6de",
  borderRadius: 5,
  fontFamily: "inherit",
  boxSizing: "border-box",
};

const checkStyle = {
  width: 16,
  height: 16,
  minWidth: 0,
  maxWidth: 16,
  margin: 0,
  padding: 0,
};

export default class ValveEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = { rows: rowsFromValve(props.valve) };
  }

  componentDidUpdate(prev) {
    if (prev.valve !== this.props.valve) {
      this.setState({ rows: rowsFromValve(this.props.valve) });
    }
  }

  setRow = (i, patch) => {
    this.setState((s) => {
      const rows = s.rows.slice();
      rows[i] = { ...rows[i], ...patch };
      return { rows };
    });
  };

  blurStart = (i) => {
    const t = parseClock(this.state.rows[i].start);
    if (t != null) this.setRow(i, { start: formatClock(t) });
  };

  blurDur = (i) => {
    const t = parseDuration(this.state.rows[i].duration);
    if (t != null) this.setRow(i, { duration: formatDur(t) });
  };

  apply = () => {
    const { valve, onApply } = this.props;
    const events = [];
    this.state.rows.forEach((r) => {
      if (!r.on) return;
      const dayMins = parseClock(r.start);
      const durMins = parseDuration(r.duration);
      if (dayMins == null || durMins == null) return;
      events.push({
        dayMins,
        durMins,
        recipe: r.recipe || "",
      });
    });
    if (onApply) onApply(valve.id, events);
  };

  render() {
    const { valve, recipes, onClose } = this.props;
    const { rows } = this.state;
    const windowed = !!(valve && valve.windowed);
    return (
      <div
        style={overlay}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && onClose) onClose();
        }}
      >
        <div style={card} role="dialog" aria-label={valve.name || valve.id}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{valve.name || valve.id}</div>
          <div style={{ fontSize: 13, color: "#6b7785", marginTop: 2 }}>
            {valve.zone || "No zone"} · irrigation program
          </div>
          <p style={{ fontSize: 13, color: "#6b7785", margin: "12px 0" }}>
            {windowed
              ? "This channel has one window. The controller decides when to water inside it. Untick the row to switch the window off."
              : "Up to six start times a day. Each has a tick to enable it, a start, a run length, and the recipe it asks the skid for."}
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "#6b7785", fontSize: 12 }}>
                <th style={{ width: 28 }} />
                <th style={{ width: 36 }}>On</th>
                <th>{windowed ? "Window start" : "Start"}</th>
                <th>{windowed ? "Window length (HH:MM)" : "Run (HH:MM)"}</th>
                <th>Recipe</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const rec = (recipes || []).find((x) => x.recipeId === r.recipe);
                return (
                  <tr key={i}>
                    <td style={{ padding: "6px 0", color: "#6b7785" }}>{i + 1}</td>
                    <td>
                      <input
                        style={checkStyle}
                        type="checkbox"
                        checked={r.on}
                        aria-label={`Enable start ${i + 1}`}
                        onChange={(e) => this.setRow(i, { on: e.target.checked })}
                      />
                    </td>
                    <td style={{ paddingRight: 8 }}>
                      <input
                        style={inputStyle}
                        type="text"
                        value={r.start}
                        placeholder="HH:MM AM"
                        aria-label={`Start time ${i + 1}`}
                        onChange={(e) => this.setRow(i, { start: e.target.value })}
                        onBlur={() => this.blurStart(i)}
                      />
                    </td>
                    <td style={{ paddingRight: 8 }}>
                      <input
                        style={inputStyle}
                        type="text"
                        value={r.duration}
                        placeholder="HH:MM"
                        aria-label={`Run length ${i + 1}`}
                        onChange={(e) => this.setRow(i, { duration: e.target.value })}
                        onBlur={() => this.blurDur(i)}
                      />
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <i
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: 3,
                            flex: "0 0 14px",
                            background: rec && rec.color ? rec.color : "transparent",
                            border: rec && rec.color ? "none" : "1px dashed #9aa4b0",
                          }}
                        />
                        <select
                          style={inputStyle}
                          value={r.recipe}
                          aria-label={`Recipe ${i + 1}`}
                          onChange={(e) => this.setRow(i, { recipe: e.target.value })}
                        >
                          <option value="">Water only</option>
                          {(recipes || []).map((rec) => (
                            <option key={rec.recipeId} value={rec.recipeId}>
                              {rec.name || rec.recipeId}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p style={{ fontSize: 13, color: "#2f5f8f", background: "#eaf2fa", borderRadius: 6, padding: "8px 10px" }}>
            Apply puts these times on the board. Nothing is written to the controller yet.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" onClick={onClose} style={btn("#fff", "#26303c", "#cfd6de")}>
              Cancel
            </button>
            <button type="button" onClick={this.apply} style={btn("#1a73e8", "#fff", "#1a73e8")}>
              Apply
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function btn(bg, color, border) {
  return {
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 600,
    padding: "7px 14px",
    borderRadius: 6,
    border: `1px solid ${border}`,
    background: bg,
    color,
    cursor: "pointer",
  };
}
